import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { micLumiq } from '@/assets';
import { Button } from '@/components';
import { Routes } from '@/constants';
import { LearningPathCardGrid } from '@/containers';
import { EVENT_CONSTANTS } from '@/services';
import { selectIsLpsListView, useAppSelector } from '@/store';
import { LearningPathWithDuration } from '@/types';

import {
  LearningPathsList,
  LearningPathsListVariants,
} from './LearningPathsList';
import { LearningPathsSectionHeader } from './LearningPathsSectionHeader';

export const LearningPathsSubpage = ({
  title,
  subtitle,
  learningPaths,
  organizationName,
  isLoading = false,
}: {
  title: string;
  subtitle?: string;
  learningPaths: LearningPathWithDuration[];
  organizationName?: string;
  isLoading?: boolean;
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const isListView = useAppSelector(selectIsLpsListView);

  return (
    <Box>
      <LearningPathsSectionHeader title={title} subtitle={subtitle} />
      {!isMd ? (
        <LearningPathsList
          learningPaths={learningPaths}
          variant={LearningPathsListVariants.HORIZONTAL}
          isLoading={isLoading}
        />
      ) : isListView ? (
        <LearningPathsList
          learningPaths={learningPaths}
          variant={LearningPathsListVariants.LIST}
          organizationName={organizationName}
          isLoading={isLoading}
        />
      ) : (
        <LearningPathCardGrid
          lps={learningPaths}
          referrer={{ page: EVENT_CONSTANTS.PAGE.LEARNING_PATHS }}
          isLoading={isLoading}
        />
      )}
      {!isLoading && learningPaths.length === 0 && (
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'white',
            borderRadius: 2,
            py: 3,
          }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              maxWidth: 400,
              mx: 'auto',
              textAlign: 'center',
            }}>
            <img src={micLumiq} alt="Mic Lumiq" width={128} height={128} />
            <Typography variant="h3">Start a new Learning Path</Typography>
            <Typography variant="body3" sx={{ color: 'text.main' }}>
              Explore our curated lists of Learning Paths to achieve your
              specific goals
            </Typography>
            <Button
              label="Explore Learning Paths"
              fullWidth={false}
              onClick={() => navigate(Routes.LEARNING_PATHS_TOPICS)}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
